import React, { useEffect, useState } from "react";

import { Grid, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Grid as DataGrid, GridColumn } from "@progress/kendo-react-grid";
import { orderBy } from "@progress/kendo-data-query";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { CurrencyCell } from "../currency-cell";
import { PercentageCell } from "../percentage-cell";
import { AccountTransferDropDown } from "../fia";
import FileDragAndDrop from "../FileDragDrop";
import DownloadTemplateCsv from "../download-csv";
import {collectErrorMessages, currencyFormatNoDecimals, getCurrentYear} from "../../shared/shared";
import IconButton from "@mui/material/IconButton";
import ErrorIcon from "@mui/icons-material/Error";
import {StyledTooltip} from "../../pages/plan-inputs";
import ValidationSignCell from "../validation-sign-cell";
import OwnerDropDown from "../owner-cell";
import OwnerJointTrustDropDown from "../owner-joint-trust-cell";
import {NumericTextBox} from "@progress/kendo-react-inputs";
import {MAX_CURRENCY_FUTURE_ADJUSMENTS} from "../../shared/constants";

const CommandCell = (deleteFn) => {
  return (props) => {
    const dataItem = props.dataItem;

    return (
      <td>
        <DeleteIcon
          onClick={() => {
            deleteFn(dataItem);
          }}
        />
      </td>
    );
  };
};

const InsuranceTypeDropDownCell = (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const options = [
    {
      value: "VOLATILITY_BUFFER",
      text: "Volatility Buffer",
    },
    {
      value: "GUARANTEED_INCOME",
      text: "Guaranteed Income",
    },
  ];

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];
  return (
    <td>
      <DropDownList
        style={{
          width: "170px",
        }}
        onChange={handleChange}
        value={options.find((c) => c.value === dataValue)}
        data={options}
        textField="text"
      />
    </td>
  );
};

const InsuranceVolatilityBuffer = (props) => {
  const dataItem = props.dataItem;

  return (
    <DataGrid
      data={[dataItem]}
      scrollable={"none"}
      editField={"inEdit"}
      onItemChange={(event) => {
        const dataItem = event.dataItem;
        const field = event.field;
        const value = event.value;

        InsuranceVolatilityBuffer.update(field)(dataItem, value);
      }}
    >
      <GridColumn
        title={"Cash Value Return"}
        field={"cashValueReturn"}
        cell={PercentageCell}
      />
      <GridColumn
        title={"Death Benefit Value"}
        field={"deathBenefit"}
        cell={CurrencyCell}
      />
      <GridColumn
        title={"Death Benefit Return"}
        field={"deathBenefitReturn"}
        cell={PercentageCell}
      />
    </DataGrid>
  );
};

const InsuranceGuaranteedIncome = (props) => {
  const initalState = {
    data: props.dataItem.insuranceDetails,
    fileName: props.dataItem.fileName !== null ? props.dataItem.fileName : null,
    messages: [],
    tempFileName: null
  }
  const [fileInput, setFileInput] = useState(initalState);

  useEffect(() => {
    InsuranceGuaranteedIncome.update("insuranceDetails")(
      {
        ...props.dataItem,
        valid: fileInput.messages.length > 0 ? false : true,
        fileName: fileInput.fileName
      },
      fileInput.data
    );
  }, [fileInput.data])

  const cleanFile = () => {
    setFileInput((v) => ({ ...v, data: null }))
  }



  return (
    <Grid container spacing={3}>
      <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
        {<DownloadTemplateCsv />}
      </Grid>
      <Grid item xs={8} >
        <Grid container>
          <Grid item xs={10} style={{ marginLeft: '125px' }} >
            <FileDragAndDrop
              valid={InsuranceGuaranteedIncome.validations[props.dataIndex]?.insuranceDetails.valid}
              className='FilesDragAndDrop'
              setFileInput={setFileInput}
              fileInput={fileInput}
              index={props.dataIndex}
              cleanFile={cleanFile}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const InsuranceYearCell = (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value
      });
    }
  };

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  if ((field === 'transferEndYear' || field === 'transferStartYear') && dataItem.insuranceType === "GUARANTEED_INCOME") return <td></td>

  if (!dataItem.inEdit) {
    return <td>
      {dataValue}
    </td>
  }

  if (props.dataItem.annuityType === "GUARANTEED_INCOME" && field === "transferEndYear") {
    return <td >
      <NumericTextBox
          label={" "}
          value={props.dataItem.transferStartYear} //value setin getPayloadFuturePlan()
          disabled={true}
          format={"'#'"}
          valid={true}
      />
    </td>
  }

  return (
      <td >
        <NumericTextBox
            label={" "}
            autoFocus="autoFocus"
            value={dataValue}
            onChange={handleChange}
            format={"'#'"}
            min={getCurrentYear()}
            max={getCurrentYear() + 100}
            valid={(dataValue >= getCurrentYear() && dataValue <= (getCurrentYear() + 100))}
        />
      </td>
  );
};

const InsuranceTransferAmount = (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value,
      });
    }
  };

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  if (dataItem.insuranceType === "GUARANTEED_INCOME")
    return <td></td>

  if (!dataItem.inEdit) {
    return <td>
      {currencyFormatNoDecimals(dataValue)}
    </td>
  }

  return (
      <td >
        <NumericTextBox
            label={" "}
            value={dataValue}
            onChange={handleChange}
            format={"c0"}
            min={0}
            max={MAX_CURRENCY_FUTURE_ADJUSMENTS}
            valid={dataValue >= 1 && dataValue <= MAX_CURRENCY_FUTURE_ADJUSMENTS}
        />
      </td>
  );
};

const InsuranceDetails = (props) => {
  const dataItem = props.dataItem;

  return dataItem.insuranceType === "VOLATILITY_BUFFER" ? (
    <InsuranceVolatilityBuffer {...props} />
  ) : (
    <InsuranceGuaranteedIncome {...props} />
  );
};

const Insurance = (props) => {
  const [state, setState] = useState({
    sort: [
      {
        field: "firstName",
        dir: "asc",
      },
    ],
    page: { skip: 0, take: 10 },
    errorMessages: [],
  });

  useEffect(() => {
    setState({
      ...state,
      errorMessages: collectErrorMessages(props.validations.insuranceTransfers, "insurance transfer"),
    })
  }, [props.validations.insuranceTransfers]);

  const [insuranceTypeVG, setInsuranceTypeVG] = useState(
    [
      {
        insuranceType: "GUARANTEED_INCOME",
        editIndex: 0
      }
    ]
  );


  const onExpandChange = (event) => {
    props.update("isExpanded")(event.dataItem, !event.dataItem.isExpanded);
  };

  const addInsurance = () => {
    const prototype = {
      accountId: null,
      accountType: "ROTH_IRA",
      cashValueReturn: 1,
      deathBenefit: 0,
      deathBenefitReturn: 0,
      insuranceDetails: null,
      insuranceDetailsValid: null,
      fileName: null,
      insuranceType: "VOLATILITY_BUFFER",
      taxType: "TAX_FREE",
      transferAmount: 0,
      transferEndYear: getCurrentYear(),
      transferStartYear: getCurrentYear(),
      transferId: null,
      owner: "CLIENT",
      editIndex: 0,
      inEdit: true,
      isExpanded: true,
      insuranceAccountId: null
    };

    props.addTransfer(prototype);
  };

  const filterTrustList = (trustName) => {
    const findTrust = props.accounts.find(item => item.owner.toLowerCase() === trustName.toLowerCase());
    return findTrust !== undefined;
  };

  const AccountTransferDDL = AccountTransferDropDown(props.accounts);
  OwnerDropDown.hasSpouse = props.client.hasSpouse;

  InsuranceVolatilityBuffer.update = props.update;
  InsuranceGuaranteedIncome.update = props.update;
  InsuranceGuaranteedIncome.validations = props.validations.insuranceTransfers;

  const insuranceTransfers = props.insuranceTransfers.map((item, index) => {
    const validation = props.validations.insuranceTransfers[index];
    let isValid = true;
    if (validation) {
      Object.keys(validation).forEach((key) => {
        if (!validation[key].valid) isValid = false;
      });
    }
    return {
      ...item,
      inEdit: true,
      isValid: isValid,
      showJointTrustOptions: true,
      listTrusts: true
    }
  });

  OwnerJointTrustDropDown.hasSpouse = props.client.hasSpouse;
  OwnerJointTrustDropDown.trustList = props.trustListing;

  const shouldShowAlternativeFundingSources = (item) => {
    return item.insuranceType === "VOLATILITY_BUFFER";
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography>Insurance</Typography>
      </Grid>
      <Grid item xs={12}>
        {(!props.readOnly && state.errorMessages.length > 0) && (
            <div className="error-tooltip-wrapper">
              <StyledTooltip
                  arrow
                  className="error-tooltip"
                  placement="right"
                  title={
                    <ul className="error-tooltip-ul">{
                      state.errorMessages.map((e, idx) => {
                        return (
                            <li key={idx}>
                              <Typography
                                  className={'default-font'}
                                  textAlign={'left'}
                                  variant={'span'}> {e}
                              </Typography>
                            </li>
                        );
                      })}
                    </ul>
                  }
              >
                <IconButton>
                  <ErrorIcon />
                </IconButton>
              </StyledTooltip>
            </div>
        )}
        <Button onClick={addInsurance}>
          <AddIcon />
          Add Insurance
        </Button>
      </Grid>
      <Grid item xs={12}>
        <DataGrid
          data={orderBy(insuranceTransfers, state.sort).slice(
            state.page.skip,
            state.page.take + state.page.skip
          )}
          skip={state.page.skip}
          take={state.page.take}
          total={props.insuranceTransfers.length}
          pageable={true}
          onPageChange={(event) => {
            setState({
              ...state,
              page: event.page,
            });
          }}
          scrollable={"none"}
          expandField={"isExpanded"}
          onExpandChange={onExpandChange}
          detail={InsuranceDetails}
          editField={"inEdit"}
          onItemChange={(event) => {
            const dataItem = event.dataItem;
            const field = event.field;
            const value = event.value;

            props.update(field)(dataItem, value);
          }}
        >
          <GridColumn title={" "} field={"isValid"} cell={ValidationSignCell} />
          <GridColumn title={"Owner"} field={"owner"} cell={(p) => <OwnerJointTrustDropDown {...p} allowNewTrusts={false} filterFn={filterTrustList} />} />
          <GridColumn
            field={"insuranceType"}
            title={"Insurance Type"}
            cell={InsuranceTypeDropDownCell}
          />

          <GridColumn
            field={"accountId"}
            title={"Funding Source"}
            cell={(props) => <AccountTransferDDL {...props} showCurrentIncomeFundingSource={shouldShowAlternativeFundingSources(props.dataItem)} showAlternativeFundingSources={shouldShowAlternativeFundingSources(props.dataItem)} />}
          />

          <GridColumn
            field={"transferAmount"}
            title={"Transfer Amount"}
            cell={InsuranceTransferAmount}
          />
          <GridColumn
            field={"transferStartYear"}
            title={"Transfer Start Year"}
            cell={InsuranceYearCell}
          />
          <GridColumn
            field={"transferEndYear"}
            title={"Transfer End Year"}
            cell={InsuranceYearCell}
          />
          <GridColumn title={" "} cell={CommandCell(props.delete)} />
        </DataGrid>
      </Grid>
    </Grid>
  );
};

export default Insurance;
